import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  initializeFirestore,
  getFirestore,
  Firestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getAnalytics, Analytics, setAnalyticsCollectionEnabled } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let firebaseApp: FirebaseApp;
let db: Firestore;
let auth: Auth;
let functions: Functions;
let storage: FirebaseStorage;
let analytics: Analytics | null = null;

const isFirstInit = getApps().length === 0;

try {
  firebaseApp = isFirstInit ? initializeApp(firebaseConfig) : getApp();
  // experimentalAutoDetectLongPolling: Firestore's default streaming WebChannel
  // transport is blocked by Safari ITP, some corporate proxies, and content
  // blockers — surfacing as "Fetch API cannot load .../Firestore/Listen/channel
  // … due to access control checks" and a realtime listener that never delivers
  // updates (writes persist server-side but the UI never refreshes). Auto-detect
  // uses streaming where it works and transparently falls back to long-polling
  // where it's blocked. initializeFirestore must run before any getFirestore call,
  // so it's gated on first init; HMR/re-init reuses the existing instance.
  db = isFirstInit
    ? initializeFirestore(firebaseApp, { experimentalAutoDetectLongPolling: true })
    : getFirestore(firebaseApp);
  auth = getAuth(firebaseApp);
  functions = getFunctions(firebaseApp, 'europe-west2');
  storage = getStorage(firebaseApp);

  // GRC-21: Only initialise Analytics when the user has accepted cookies.
  // The CookieConsent component stores the choice in localStorage.
  const cookieConsent =
    typeof window !== 'undefined' ? localStorage.getItem('planacle_cookie_consent') : null;

  if (cookieConsent === 'accepted') {
    analytics = getAnalytics(firebaseApp);
    setAnalyticsCollectionEnabled(analytics, true);
  } else if (cookieConsent === 'declined') {
    // Explicitly init but disable collection so no data is sent
    analytics = getAnalytics(firebaseApp);
    setAnalyticsCollectionEnabled(analytics, false);
  }
  // If no choice yet (null), analytics is not initialised at all

  // Connect to Emulators explicitly if enabled (bypassing DEV check for CI preview builds).
  // Only run on first init — HMR re-evaluations must skip this or the SDK throws.
  if (isFirstInit && import.meta.env.VITE_USE_EMULATORS === 'true') {
    // Use location.hostname to connect to the correct IP if on network
    const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

    connectFirestoreEmulator(db, host, 8080);
    connectAuthEmulator(auth, `http://${host}:9099`);
    connectFunctionsEmulator(functions, host, 5001);
    connectStorageEmulator(storage, host, 9199);
  }
} catch (error) {
  console.error('Firebase initialization failed:', error);
}

export { db, auth, functions, storage, analytics };
